// Bootstrap: wire up styles, the router, the theme toggle, the responsive nav, and the
// PWA/data layers. Keep this thin; features live in their own modules. The ORDER of the
// calls at the bottom is load-bearing (see the notes there).

import "./styles/index.css";
// Register the app's custom elements before the first view renders, so they upgrade as
// soon as the router mounts them.
import "./components/register.js";
import { startRouter } from "./router/router.js";
import { store } from "./store/app-state.js";
import { initFavoritesPersistence } from "./store/persistence.js";
import { loadSchedule } from "./store/schedule-loader.js";
import { initFooter } from "./footer.js";
// The reconnect refetch depends on pwa.js's onNetworkChange, so it lands with the SW/offline layer.
import { initData } from "./store/schedule-loader.js";
import { initPwa, canInstall, onInstallAvailabilityChange, promptInstall } from "./pwa.js";
import { attachEventDevtools } from "./store/devtools.js";

// DEV visibility: log every event the app listens for as a clickable line. Wire it FIRST,
// before any listener is registered below, so nothing is missed. DCE'd from prod.
if (import.meta.env.DEV) {
  attachEventDevtools();
}

const THEME_KEY = "devconf-theme";

function currentTheme() {
  const override = document.documentElement.getAttribute("data-theme");
  if (override === "dark" || override === "light") {
    return override;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function initThemeToggle() {
  const toggle = document.getElementById("theme-toggle");
  if (!(toggle instanceof HTMLButtonElement)) {
    return;
  }

  // The label describes the ACTION and flips with the theme, so it doubles as the
  // accessible name (screen readers hear the change) and the hover/focus tooltip. A static
  // "Toggle dark mode" would convey neither the current state nor that anything changed.
  const syncLabel = () => {
    const label = currentTheme() === "dark" ? "Switch to light theme" : "Switch to dark theme";
    toggle.setAttribute("aria-label", label);
    toggle.title = label;
  };
  syncLabel();

  toggle.addEventListener("click", () => {
    const next = currentTheme() === "dark" ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", next);
    syncLabel();
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      /* ignore: storage may be unavailable */
    }
  });
}

function initNavToggle() {
  const toggle = document.getElementById("nav-toggle");
  const nav = document.getElementById("primary-nav");
  if (!(toggle instanceof HTMLButtonElement) || !(nav instanceof HTMLElement)) {
    return;
  }

  const mql = window.matchMedia("(max-width: 47.99rem)");

  const setOpen = (open) => {
    toggle.setAttribute("aria-expanded", String(open));
    toggle.setAttribute("aria-label", open ? "Close navigation menu" : "Open navigation menu");
    nav.hidden = !open;
  };

  // Nav is always visible on wide screens; collapse it open there, closed on narrow.
  const applyViewport = () => setOpen(!mql.matches ? true : false);
  applyViewport();
  mql.addEventListener("change", applyViewport);

  toggle.addEventListener("click", () => {
    setOpen(toggle.getAttribute("aria-expanded") !== "true");
  });

  // On narrow screens, close the menu after following a link.
  nav.addEventListener("click", (event) => {
    if (mql.matches && event.target instanceof Element && event.target.closest("a")) {
      setOpen(false);
    }
  });
}

// The header is `position: fixed` (see layout.css), so it's out of flow and `.app-main`
// reserves its height via `--header-total-h`. Measure the REAL rendered border-box height
// and publish it on :root so the reserved space tracks the header exactly (nav wrap on
// narrow, font scaling, any future change) instead of a hand-maintained magic number. The
// CSS fallback in tokens.css covers the frame before this first fires.
function initHeaderHeightVar() {
  const header = document.querySelector(".app-header");
  if (!(header instanceof HTMLElement) || typeof ResizeObserver === "undefined") {
    return;
  }
  const publish = () => {
    // border-box height (min-block-size + padding + border): the exact flow space to reserve.
    document.documentElement.style.setProperty("--header-total-h", `${header.offsetHeight}px`);
  };
  new ResizeObserver(publish).observe(header);
  publish();
}

// Offline is surfaced by the /about Status panel (and the browser's own chrome), not a
// header badge, so there's no connectivity wiring here.
function initPwaHeader() {
  const installBtn = document.getElementById("install-btn");
  if (installBtn instanceof HTMLButtonElement) {
    const sync = (available) => {
      installBtn.hidden = !available;
    };
    sync(canInstall());
    onInstallAvailabilityChange(sync);
    installBtn.addEventListener("click", () => void promptInstall());
  }
}

initThemeToggle();
initNavToggle();
initHeaderHeightVar();
// Capture install/connectivity/SW events as early as possible, then reflect them in the
// header. SW registration itself is deferred to window `load` (and to production builds
// only) inside initPwa().
initPwa();
initPwaHeader();
// Hydrate favorites from localStorage BEFORE the first view renders, so the
// schedule/agenda paint with the saved stars already applied.
initFavoritesPersistence(store);
initFooter();
// Warm the core dataset at boot, independent of the entry route: landing on /about or
// deep-linking to /session/:id still loads the schedule in the background. Fire-and-forget
// and DEDUPED: the / and /agenda views' own mount kick joins this same in-flight load
// rather than racing a second one, so there's one load cycle and no double skeleton.
void loadSchedule();
// One global reconnect refetch, wired once: any view that failed or went stale while
// offline self-heals when the network returns (also covers /agenda).
initData();
startRouter();
